import { IconStar } from '@tabler/icons-react-native'
import { clsx } from 'clsx'
import { Image } from 'expo-image'
import { type ImagePickerAsset } from 'expo-image-picker'
import { compact, concat, fill } from 'lodash'
import { forwardRef, useCallback, useMemo } from 'react'
import { FlatList, type FlatListProps, type ListRenderItemInfo } from 'react-native'

import placeholder from '@/assets/images/placeholder.png'
import { HStack, Icon, LoadingView, Pressable, VStack, type PressableProps } from '@/components'
import { useIsMobileScreen } from '@/hooks'
import { COLOR_BRAND_YELLOW_500, COLOR_WHITE } from '@/lib'

import { type MinimalPhoto } from './types'

export interface PhotoGridProps extends Omit<FlatListProps<MinimalPhoto>, 'data' | 'renderItem'> {
  data: MinimalPhoto[]
  gap?: number
  itemClassName?: PressableProps['className']
  isUploading?: boolean
  onPhotoPress?: (photoId: string) => void
  size?: 'sm' | 'lg'
  uploadingAsset?: ImagePickerAsset | null
}

export type PhotoGridRef = FlatList<MinimalPhoto>

export const PhotoGrid = forwardRef<PhotoGridRef, PhotoGridProps>(
  (
    {
      className,
      columnWrapperClassName,
      contentContainerClassName,
      data,
      extraData,
      gap = 16,
      isUploading,
      itemClassName,
      numColumns: _numColumns,
      onPhotoPress,
      size = 'sm',
      uploadingAsset,
      ...props
    },
    ref
  ) => {
    const isMobileScreen = useIsMobileScreen()
    const photoCount = data.length ?? 0
    const numColumns = _numColumns || (isMobileScreen ? 2 : 3)

    return (
      <FlatList
        bounces={false}
        contentContainerClassName={clsx('gap-4', contentContainerClassName)}
        columnWrapperClassName={clsx('gap-4', columnWrapperClassName)}
        data={useMemo(() => {
          // add in a grid item for when we are uploading
          const _data = compact(concat(data, isUploading ? { rowId: 'uploading', source: uploadingAsset } : undefined))
          const remainder = _data.length % numColumns
          const empties = remainder ? numColumns - remainder : 0

          if (empties) {
            return concat(_data, fill(new Array(empties), { rowId: 'spacer', source: null }))
          }

          return _data
        }, [data, isUploading, numColumns, uploadingAsset])}
        extraData={{ itemClassName, onPhotoPress, photoCount, uploadingAsset, ...extraData }}
        key={`photogrid-${numColumns}`}
        keyExtractor={useCallback((item: MinimalPhoto, index: number) => item.rowId ?? `photo-${index}`, [])}
        numColumns={numColumns}
        ref={ref}
        renderItem={useCallback(
          ({ item, index }: ListRenderItemInfo<MinimalPhoto>) => {
            const { blurhash, description, isPrimary, rowId, source } = item

            if (rowId === 'spacer') {
              return <VStack className='aspect-square flex-1' />
            }

            const isUpload = rowId === 'uploading'

            return (
              <Pressable
                accessibilityState={{
                  busy: isUpload
                }}
                className={clsx(
                  'flex-1 overflow-hidden rounded-lg border-[0.5px] border-brand-grey-400 bg-white p-1',
                  itemClassName
                )}
                disabled={isUpload}
                onPress={() => rowId && onPhotoPress?.(rowId)}
                testID={isUpload ? 'PhotoGrid.uploadingTile' : `PhotoGrid.photo-${index}`}
              >
                <Image
                  alt={description ?? `Photo ${index + 1} of ${photoCount}`}
                  className='aspect-square w-full overflow-hidden rounded'
                  cachePolicy='memory-disk'
                  contentFit='cover'
                  contentPosition='center'
                  placeholder={uploadingAsset ? { uri: uploadingAsset.uri } : blurhash ? { blurhash } : placeholder}
                  placeholderContentFit='cover'
                  source={source}
                />
                {isUpload ? <LoadingView className='absolute inset-x-0 inset-y-0 items-center justify-center' /> : null}
                {!isUpload && isPrimary ? (
                  <HStack
                    accessible
                    accessibilityLabel='Primary photo'
                    className='absolute left-2 top-2'
                  >
                    <Icon
                      as={IconStar}
                      className='h-6 w-6'
                      fill={COLOR_BRAND_YELLOW_500}
                      stroke={COLOR_WHITE}
                      strokeWidth={1}
                    />
                  </HStack>
                ) : null}
              </Pressable>
            )
          },
          [itemClassName, onPhotoPress, photoCount, uploadingAsset]
        )}
        {...props}
      />
    )
  }
)

PhotoGrid.displayName = 'PhotoGrid'
